import { useEffect, useState } from 'react'
import { Check } from 'lucide-react'
import {
  CHIEF_COMPLAINTS,
  DIAGNOSIS_OPTIONS,
  TREATMENT_OPTIONS,
  formatPresetLine,
} from '../data/toothTaxiPreset.js'

/**
 * Tooth Taxi Preset picker — the shared Chief Complaint / Diagnosis / Treatment
 * selection body. Owns its own selection state and reports the formatted output
 * to the parent via onChange (fired on every change). Parents supply the modal
 * chrome (header / footer / Apply button) and decide what to do with the output:
 *   - ClinicalEditor → fill the cc field + an entry's Oral Exam & Treatment (PE)
 *   - DocumentGenerator → fill the {{chief_complaint}}/{{diagnosis}}/{{treatment}}
 *     placeholders on the "ตรวจสุขภาพ Tooth taxi" PDF
 *
 * onChange receives the full output shape (see computeOutput).
 */

// Selection state is keyed by option index: { sel, input, base, subbase, text }.
function presetToggle(setState, i) {
  setState((prev) => ({ ...prev, [i]: { ...(prev[i] || {}), sel: !(prev[i]?.sel) } }))
}
function presetPatch(setState, i, patchObj) {
  setState((prev) => ({ ...prev, [i]: { ...(prev[i] || {}), ...patchObj } }))
}

function buildLines(options, state) {
  const lines = []
  options.forEach((opt, i) => {
    if (!state[i]?.sel) return
    const line = formatPresetLine(opt, state[i])
    if (line) lines.push(line)
  })
  return lines
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// Compute every output shape a caller might need from the raw selections.
export function computeOutput(chief, chiefOther, diag, treat) {
  const chiefList = CHIEF_COMPLAINTS.filter((c) => chief.has(c))
  const other = String(chiefOther || '').trim()
  if (other) chiefList.push(other)
  const diagLines = buildLines(DIAGNOSIS_OPTIONS, diag)
  const treatLines = buildLines(TREATMENT_OPTIONS, treat)
  return {
    chiefList,
    chief_complaint: chiefList.join(', '),
    diagLines,
    treatLines,
    // Plain multi-line (for the PDF renderer's renderBlock_).
    diagnosis: diagLines.map((l) => `• ${l}`).join('\n'),
    treatment: treatLines.map((l) => `• ${l}`).join('\n'),
  }
}

// Build the rich-text (HTML) block for the Clinical "Oral Exam & Treatment" field:
//   **Diagnosis**            **Treatment**
//   • …                      • …
export function buildOralExamHtml(out) {
  const parts = []
  if (out.diagLines.length) {
    parts.push('<b>Diagnosis</b>')
    out.diagLines.forEach((l) => parts.push(`• ${escapeHtml(l)}`))
  }
  if (out.treatLines.length) {
    if (parts.length) parts.push('') // blank line between the two blocks
    parts.push('<b>Treatment</b>')
    out.treatLines.forEach((l) => parts.push(`• ${escapeHtml(l)}`))
  }
  return parts.join('<br>')
}

const INPUT_CLS =
  'w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-300'

function PresetSection({ title, options, state, setState }) {
  return (
    <div>
      <div className="text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1.5">{title}</div>
      <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
        {options.map((opt, i) => {
          const st = state[i] || {}
          const on = !!st.sel
          const tail = opt.on ? ' on …' : opt.with ? ' with …' : ''
          return (
            <div key={i} className={on ? 'bg-brand-50/40' : ''}>
              <button
                type="button"
                onClick={() => presetToggle(setState, i)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50/70 transition-colors"
              >
                <span className={`w-[17px] h-[17px] rounded-[5px] flex items-center justify-center flex-none ring-1 transition-colors ${
                  on ? 'bg-brand-600 ring-brand-600 text-white' : 'bg-white ring-slate-300 text-transparent'
                }`}>
                  <Check size={11} strokeWidth={3} />
                </span>
                <span className={`text-[14px] ${on ? 'font-medium text-slate-800' : 'text-slate-600'}`}>
                  {opt.label}<span className="text-slate-400">{tail}</span>
                </span>
              </button>
              {on && (opt.on || opt.with || opt.other || opt.extra) && (
                <div className="px-3 pb-2.5 pl-[42px] space-y-1.5">
                  {(opt.on || opt.with) && (
                    <input
                      autoFocus
                      value={st.input || ''}
                      onChange={(e) => presetPatch(setState, i, { input: e.target.value })}
                      placeholder={opt.on ? 'ซี่ฟัน เช่น 36, 37' : 'ระบุ'}
                      className={INPUT_CLS}
                    />
                  )}
                  {opt.other && (
                    <input
                      autoFocus
                      value={st.text || ''}
                      onChange={(e) => presetPatch(setState, i, { text: e.target.value })}
                      placeholder="ระบุรายละเอียด"
                      className={INPUT_CLS}
                    />
                  )}
                  {opt.extra && opt.extra.map((ex) => (
                    <input
                      key={ex.key}
                      value={st[ex.key] || ''}
                      onChange={(e) => presetPatch(setState, i, { [ex.key]: e.target.value })}
                      placeholder={`${ex.label} …`}
                      className={INPUT_CLS}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function ToothTaxiPresetPicker({ onChange }) {
  const [chief, setChief] = useState(() => new Set())
  const [chiefOther, setChiefOther] = useState('')
  const [diag, setDiag] = useState({})
  const [treat, setTreat] = useState({})

  useEffect(() => {
    onChange?.(computeOutput(chief, chiefOther, diag, treat))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chief, chiefOther, diag, treat])

  function toggleChief(c) {
    setChief((prev) => {
      const next = new Set(prev)
      if (next.has(c)) next.delete(c); else next.add(c)
      return next
    })
  }

  const diagCount = Object.values(diag).filter((s) => s?.sel).length
  const treatCount = Object.values(treat).filter((s) => s?.sel).length
  const chipCls = (on) =>
    `px-2.5 py-1 rounded-lg text-[13.5px] font-medium ring-1 transition-colors ${
      on ? 'bg-brand-50 text-brand-700 ring-brand-200' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
    }`

  return (
    <div>
      {/* Chief Complaint */}
      <div className="mb-4">
        <div className="text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1.5">Chief Complaint</div>
        <div className="flex flex-wrap gap-1.5">
          {CHIEF_COMPLAINTS.map((c) => (
            <button key={c} type="button" onClick={() => toggleChief(c)} className={chipCls(chief.has(c))}>{c}</button>
          ))}
        </div>
        <input
          value={chiefOther}
          onChange={(e) => setChiefOther(e.target.value)}
          placeholder="อื่นๆ — ระบุเพิ่มเติม"
          className={`mt-2 ${INPUT_CLS}`}
        />
      </div>

      {/* Diagnosis + Treatment */}
      <div className="grid sm:grid-cols-2 gap-4">
        <PresetSection title={`Diagnosis${diagCount ? ` · ${diagCount}` : ''}`} options={DIAGNOSIS_OPTIONS} state={diag} setState={setDiag} />
        <PresetSection title={`Treatment${treatCount ? ` · ${treatCount}` : ''}`} options={TREATMENT_OPTIONS} state={treat} setState={setTreat} />
      </div>
    </div>
  )
}
